/**
 * Salesperson ledger — one line per deal, per credited team member.
 *
 * This is the report the business actually asked for: pick a person, pick a
 * period, and see every deal they were credited on with the VIN, the vehicle,
 * what it sold for, the margin and their commission — with totals and an export.
 *
 * Two deliberate design decisions:
 *
 * 1. **Two bases, side by side.** The existing commission figure is computed
 *    from `deals.gross_margin`, which is written once at deal creation as
 *    (Autograb market estimate − purchase price). It ignores transport and
 *    repairs, has no Load Fee, and is not the realised resale. Rather than
 *    silently changing what anyone gets paid, this reports the current
 *    "as calculated" figure alongside a "realised" figure built from the
 *    invoice and the capitalised costs, so the gap is visible on real deals
 *    before anyone commits to changing the basis.
 *
 * 2. **The rate comes from the attribution snapshot**, never the member's
 *    current rate, so editing someone's rate cannot retrospectively restate
 *    historical commission.
 */

import { supabase } from '../lib/supabase'
import { buyingFeeFor, commissionEntitlement, commissionTerms, netMarginOf, termsLabel } from '../lib/margin'
import { Deal, TeamMember, DealAttribution } from '../types'
import { SOLD_STATUSES } from '../lib/dealLifecycle'

/**
 * Buying fee (§3.2 of the sales workflow).
 *
 * The spec is "$500 standard; $250 if loss". It was a flat $250 here, which
 * understated the cost base on every profitable deal and so overstated both net
 * profit and the commission paid on it.
 *
 * The catch is that the fee depends on whether the deal made a loss, and the
 * loss depends on the fee. Resolving that by iteration would be unstable near
 * break-even, so the test is applied to the margin BEFORE the fee: a deal that
 * is already under water pays the reduced fee, and a deal that is above water
 * pays the standard one. That also matches how it reads in the document —
 * the reduced fee is relief on a bad deal, not a rounding rule.
 */
// The fee, the margin rule and the commission rule all live in src/lib/margin.ts
// now — one definition, so this report and the commission engine can no longer
// disagree about what a deal earned. Re-exported here because several modules
// already import them from this service.
export {
  BUYING_FEE_STANDARD,
  BUYING_FEE_ON_LOSS,
  LOAD_FEE,
  buyingFeeFor,
} from '../lib/margin'

export type SaleDateBasis = 'sold_at' | 'invoice_date' | 'paid_at' | 'created_at'

export interface LedgerLine {
  dealId: string
  dealRef: string
  vin: string
  vehicle: string
  sellerName: string
  buyerName: string
  status: string

  memberId: string
  memberName: string
  memberRole: string
  /** Rate snapshotted on the attribution at the time it was created. Percent basis only. */
  rate: number
  /** "25%" or "$500 fixed" — how this person's terms read on this deal. */
  termsLabel: string

  saleDate: string | null
  saleDateBasis: SaleDateBasis | null

  buyIn: number
  capitalised: number
  loadFee: number

  /** What the report currently pays on: estimated sale price. */
  grossSaleEstimated: number
  /** Realised resale from the invoice, when one exists. */
  grossSaleRealised: number | null
  /** True when grossSaleRealised came from an actual invoice. */
  hasInvoice: boolean

  /** deals.gross_margin — the figure the existing commission uses. */
  marginAsCalculated: number
  /** realised sale − buy-in − capitalised − load fee. Null with no invoice. */
  marginRealised: number | null

  commissionAsCalculated: number
  commissionRealised: number | null
}

export interface LedgerTotals {
  deals: number
  grossSaleEstimated: number
  grossSaleRealised: number
  marginAsCalculated: number
  marginRealised: number
  commissionAsCalculated: number
  commissionRealised: number
  linesWithInvoice: number
}

export interface LedgerFilters {
  memberId?: string | null
  from?: string | null
  to?: string | null
  /** Restrict to deals that have actually reached a revenue stage. */
  realisedOnly?: boolean
}

export interface SalesLedger {
  lines: LedgerLine[]
  totals: LedgerTotals
  members: TeamMember[]
  /** Deals in range with no attribution — invisible to every commission report. */
  unattributedCount: number
  unattributedValue: number
}

const dYear = (d: Deal) => d.autograb_year ?? d.vehicle_year
const dMake = (d: Deal) => d.autograb_make || d.vehicle_make || ''
const dModel = (d: Deal) => d.autograb_model || d.vehicle_model || ''

export const vehicleLabel = (d: Deal) =>
  [dYear(d), dMake(d), dModel(d)].filter(Boolean).join(' ').trim() || 'Vehicle details unavailable'

export const vinOf = (d: Deal) => d.autograb_vin || d.vehicle_vin || ''
export const dealRef = (id: string) => (id.length > 8 ? id.slice(0, 8).toUpperCase() : id)

/**
 * Statuses that represent a deal the business has actually realised value on.
 *
 * Hand-written as ['sold', 'closed'] before 10 September, when sold_in_transit and
 * sold_delivered became where sales land. Neither was in it, so the realised-only
 * filter dropped every sale made through the Sale Details Workspace.
 *
 * The canonical list lives in lib/dealLifecycle.
 */
const REALISED_STATUSES: readonly string[] = SOLD_STATUSES

export const roleLabel = (role: string) =>
  role === 'buyer_sourcer' ? 'Buyer-Sourcer' : role === 'sales' ? 'Sales' : 'Spotter'

/**
 * Pick the best available sale date and say which one it is, so the report can
 * be honest about the basis rather than quietly using `updated_at`.
 */
function resolveSaleDate(d: Deal, invoice: any): { date: string | null; basis: SaleDateBasis | null } {
  if (d.sold_at) return { date: d.sold_at, basis: 'sold_at' }
  if (invoice?.invoice_date) return { date: invoice.invoice_date, basis: 'invoice_date' }
  if (d.paid_at) return { date: d.paid_at, basis: 'paid_at' }
  if (d.created_at) return { date: d.created_at, basis: 'created_at' }
  return { date: null, basis: null }
}

export async function listLedgerMembers(): Promise<TeamMember[]> {
  const { data, error } = await supabase.from('team_members').select('*').order('name')
  if (error) throw error
  return (data || []) as TeamMember[]
}

export async function loadSalesLedger(filters: LedgerFilters = {}): Promise<SalesLedger> {
  const [d, m, a, inv] = await Promise.all([
    supabase.from('deals').select('*').is('deleted_at', null),
    supabase.from('team_members').select('*').order('name'),
    supabase.from('deal_attributions').select('*'),
    supabase.from('invoices').select('*'),
  ])
  for (const r of [d, m, a, inv]) if (r.error) throw r.error

  const deals = (d.data || []) as Deal[]
  const members = (m.data || []) as TeamMember[]
  const attributions = (a.data || []) as DealAttribution[]
  const invoices = (inv.data || []) as any[]

  const memberById = new Map(members.map((x) => [x.id, x]))
  const dealById = new Map(deals.map((x) => [x.id, x]))

  // One invoice per deal — the most recent wins if there is more than one.
  const invoiceByDeal = new Map<string, any>()
  for (const i of invoices) {
    const existing = invoiceByDeal.get(i.deal_id)
    if (!existing || new Date(i.invoice_date || 0) > new Date(existing.invoice_date || 0)) {
      invoiceByDeal.set(i.deal_id, i)
    }
  }

  const from = filters.from ? new Date(`${filters.from}T00:00:00`) : null
  const to = filters.to ? new Date(`${filters.to}T23:59:59.999`) : null

  const inRange = (iso: string | null) => {
    if (!iso) return !from && !to
    const t = new Date(iso).getTime()
    if (from && t < from.getTime()) return false
    if (to && t > to.getTime()) return false
    return true
  }

  const lines: LedgerLine[] = []
  const attributedDealIds = new Set<string>()

  for (const attr of attributions) {
    const deal = dealById.get(attr.deal_id)
    if (!deal) continue
    if (filters.memberId && attr.team_member_id !== filters.memberId) continue
    if (filters.realisedOnly && !REALISED_STATUSES.includes(deal.deal_status)) continue

    const invoice = invoiceByDeal.get(deal.id) || null
    const { date: saleDate, basis } = resolveSaleDate(deal, invoice)
    if (!inRange(saleDate)) continue

    attributedDealIds.add(deal.id)

    const buyIn = Number(deal.valuation_amount || 0)
    const capitalised = Number(deal.capitalised_transport || 0) + Number(deal.capitalised_repairs || 0)
    const grossSaleEstimated = Number(deal.estimated_sale_price ?? deal.autograb_market_value ?? 0)
    const realisedSale = invoice ? Number(invoice.total_amount ?? invoice.purchase_price ?? 0) : null

    const marginAsCalculated = Number(deal.gross_margin ?? 0)

    // The fee is decided on the margin before it is charged — see buyingFeeFor.
    // A deal can also carry an explicit fee, which wins: someone may have agreed
    // a different number and it should not be silently overwritten.
    const marginBeforeFee =
      realisedSale !== null ? realisedSale - buyIn - capitalised : null
    const buyingFee =
      deal.buying_fee != null ? Number(deal.buying_fee) : buyingFeeFor(marginBeforeFee)
    const marginRealised = marginBeforeFee === null ? null : marginBeforeFee - buyingFee
    // Commission is a share of the NET margin — Nick, 31 Aug 2026.
    //
    // This column used to be `gross_margin * rate`: the estimate minus the
    // purchase price, written once at deal creation, with transport,
    // reconditioning and the buying fee all ignored. That overstated every
    // commission by the cost of getting the car ready and moved. Where a deal
    // has not settled there is no realised sale, so the same subtraction runs
    // against the estimate instead — buy-in, capitalised costs and the buying
    // fee all come off before anyone's percentage is applied.
    //
    // netProfitOf in commissionService stays the definition of record; this
    // mirrors it for deals that have not sold yet.
    const marginProjected = netMarginOf(deal)

    // The snapshot is authoritative; fall back to the member's current terms only
    // when the attribution somehow carries none, and never silently rewrite it.
    // Terms, not just a rate, since 13 Sep: a member can be on a flat fee per deal.
    const terms = attr.basis || attr.rate != null
      ? commissionTerms(attr)
      : commissionTerms(memberById.get(attr.team_member_id))
    const rate = terms.rate

    lines.push({
      dealId: deal.id,
      dealRef: dealRef(deal.id),
      vin: vinOf(deal),
      vehicle: vehicleLabel(deal),
      sellerName: deal.seller_name || '',
      buyerName: invoice?.buyer_name || deal.buyer_name || '',
      status: deal.deal_status,

      memberId: attr.team_member_id,
      memberName: memberById.get(attr.team_member_id)?.name || '—',
      memberRole: roleLabel(attr.role),
      rate,
      termsLabel: termsLabel(terms),

      saleDate,
      saleDateBasis: basis,

      buyIn,
      capitalised,
      loadFee: buyingFee,

      grossSaleEstimated,
      grossSaleRealised: realisedSale,
      hasInvoice: !!invoice,

      marginAsCalculated,
      marginRealised,

      // "As calculated" mirrors the existing engine exactly, losses floored to
      // zero, so this column always reconciles to today's figures.
      // A loss is shared at the same rate, as a deduction — the engine does the
      // same. Flooring it at zero meant Direct Auto wore every bad deal alone
      // while paying out on every good one.
      commissionAsCalculated: commissionEntitlement(marginProjected, terms).net,
      // The realised column carries losses through as a negative share, which is
      // what Framework H.4 specifies.
      commissionRealised:
        marginRealised === null ? null : commissionEntitlement(marginRealised, terms).net,
    })
  }

  lines.sort((x, y) => new Date(y.saleDate || 0).getTime() - new Date(x.saleDate || 0).getTime())

  const totals: LedgerTotals = {
    deals: new Set(lines.map((l) => l.dealId)).size,
    grossSaleEstimated: sum(lines.map((l) => l.grossSaleEstimated)),
    grossSaleRealised: sum(lines.map((l) => l.grossSaleRealised ?? 0)),
    marginAsCalculated: sum(lines.map((l) => l.marginAsCalculated)),
    marginRealised: sum(lines.map((l) => l.marginRealised ?? 0)),
    commissionAsCalculated: sum(lines.map((l) => l.commissionAsCalculated)),
    commissionRealised: sum(lines.map((l) => l.commissionRealised ?? 0)),
    linesWithInvoice: lines.filter((l) => l.hasInvoice).length,
  }

  // Deals nobody is credited on are invisible to every commission report, which
  // is a silent gap worth surfacing rather than leaving people to discover.
  const unattributed = deals.filter((deal) => {
    if (attributions.some((x) => x.deal_id === deal.id)) return false
    if (filters.realisedOnly && !REALISED_STATUSES.includes(deal.deal_status)) return false
    const invoice = invoiceByDeal.get(deal.id) || null
    return inRange(resolveSaleDate(deal, invoice).date)
  })

  return {
    lines,
    totals,
    members,
    unattributedCount: unattributed.length,
    unattributedValue: sum(
      unattributed.map((x) => Number(x.estimated_sale_price ?? x.autograb_market_value ?? 0))
    ),
  }
}

function sum(xs: number[]) {
  return xs.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0)
}

/** Quick period presets, resolved in the browser's local timezone. */
export function periodPreset(key: 'mtd' | 'qtd' | 'ytd' | 'last_month' | 'all'): { from: string | null; to: string | null } {
  const now = new Date()
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  switch (key) {
    case 'mtd':
      return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) }
    case 'qtd': {
      const q = Math.floor(now.getMonth() / 3) * 3
      return { from: iso(new Date(now.getFullYear(), q, 1)), to: iso(now) }
    }
    case 'ytd':
      return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      return { from: iso(start), to: iso(end) }
    }
    default:
      return { from: null, to: null }
  }
}

export const SALE_DATE_BASIS_LABEL: Record<SaleDateBasis, string> = {
  sold_at: 'Sold date',
  invoice_date: 'Invoice date',
  paid_at: 'Paid date',
  created_at: 'Created date',
}

/** CSV for the current view — exactly the rows and columns on screen. */
export function ledgerToCsv(lines: LedgerLine[], totals: LedgerTotals): string {
  const head = [
    'Sale date', 'Date basis', 'Deal ref', 'VIN', 'Vehicle', 'Seller', 'Buyer', 'Status',
    'Salesperson', 'Role', 'Rate %',
    'Buy-in', 'Capitalised costs', 'Load fee',
    'Gross sale (estimated)', 'Gross sale (realised)',
    'Margin (as calculated)', 'Margin (realised)',
    'Commission (as calculated)', 'Commission (realised)',
  ]

  const esc = (v: any) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const n = (v: number | null) => (v === null ? '' : v.toFixed(2))

  const rows = lines.map((l) =>
    [
      l.saleDate ? l.saleDate.slice(0, 10) : '',
      l.saleDateBasis ? SALE_DATE_BASIS_LABEL[l.saleDateBasis] : '',
      l.dealRef, l.vin, l.vehicle, l.sellerName, l.buyerName, l.status,
      l.memberName, l.memberRole, (l.rate * 100).toFixed(1),
      n(l.buyIn), n(l.capitalised), n(l.loadFee),
      n(l.grossSaleEstimated), n(l.grossSaleRealised),
      n(l.marginAsCalculated), n(l.marginRealised),
      n(l.commissionAsCalculated), n(l.commissionRealised),
    ].map(esc).join(',')
  )

  const totalRow = [
    '', '', `TOTAL (${totals.deals} deals)`, '', '', '', '', '', '', '', '',
    '', '', '',
    n(totals.grossSaleEstimated), n(totals.grossSaleRealised),
    n(totals.marginAsCalculated), n(totals.marginRealised),
    n(totals.commissionAsCalculated), n(totals.commissionRealised),
  ].map(esc).join(',')

  return [head.map(esc).join(','), ...rows, totalRow].join('\n')
}

/**
 * Trigger a browser download.
 *
 * The anchor is attached to the document and the object URL is revoked on a
 * later tick — the previous helper did neither, which works in Chrome but can
 * silently produce no file in Firefox.
 */
export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  window.setTimeout(() => {
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 1000)
}
